<template>
  <div class="fortune-form">
    <!-- 背景装饰元素 -->
    <div class="background-elements">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>
    
    <div class="form-container">
      <!-- 头部区域 -->
      <div class="form-header">
        <div class="form-logo">
          <div class="form-title">{{ $t('form.title') }}</div>
        </div>
        
        <!-- 语言选择器 - 移动到标题右侧 -->
        <div class="language-selector-wrapper">
          <select v-model="selectedLanguage" @change="changeLanguage" class="language-selector">
            <option value="zh">中文</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
          <i class="fa fa-globe language-icon"></i>
        </div>
      </div>
      
      <!-- 表单主体 -->
      <form class="form-body" @submit.prevent="handleSubmit">
        <!-- 姓名输入 -->
        <div class="input-group">
          <label for="realName" class="input-label">
            <i class="fa fa-user input-icon"></i>
            {{ $t('form.name') }}
          </label>
          <input
            type="text"
            id="realName"
            v-model="form.name"
            :placeholder="$t('form.namePlaceholder')"
            required
            class="input-field"
          />
        </div>
        
        <!-- 性别选择 -->
        <div class="input-group">
          <label class="input-label">
            <i class="fa fa-venus-mars input-icon"></i>
            {{ $t('form.gender') }}
          </label>
          <div class="gender-options">
            <div class="gender-option" :class="{ active: form.gender === 'male' }" @click="form.gender = 'male'">
              <div class="gender-option-icon">
                <i class="fa fa-mars"></i>
              </div>
              <div class="gender-option-label">{{ $t('form.genderMale') }}</div>
            </div>
            <div class="gender-option" :class="{ active: form.gender === 'female' }" @click="form.gender = 'female'">
              <div class="gender-option-icon">
                <i class="fa fa-venus"></i>
              </div>
              <div class="gender-option-label">{{ $t('form.genderFemale') }}</div>
            </div>
          </div>
        </div>
        

<!-- 出生日期输入 -->
  <div class="input-group">
    <label class="input-label">
      <i class="fa fa-calendar input-icon"></i>
      {{ $t('form.birthDate') }}
    </label>
    <div class="datetime-inputs">
      <!-- 🔥 使用原生 datetime-local 输入，支持日期和时间选择 -->
      <input
        type="datetime-local"
        v-model="birthDateTime"
        :min="minDate + 'T00:00'"
        :max="maxDate + 'T23:59'"
        :placeholder="dateTimePlaceholder"
        :lang="dateInputLang"
        class="input-field datetime-input"
        @change="handleDateTimeChange"
      />
    </div>
  </div>
  


  <!-- 出生地2选择（vue3-country-region-select 内置数据） -->
  <div class="input-group">
    <label class="input-label">
      <i class="fa fa-globe-americas input-icon"></i>
      {{ $t('form.birthPlace2') || '出生地' }}
    </label>
    <div class="birthplace-selector2">
      <country-select
        v-model="countrySelect2.country"
        :country="countrySelect2.country"
        :countryName="true"
        class-name="input-field country-select-field"
        :placeholder="$t('form.selectCountry') || '选择国家'"
      />
      <region-select
        v-model="countrySelect2.region"
        :country="countrySelect2.country"
        :region="countrySelect2.region"
        :countryName="true"
        :regionName="true"
        class-name="input-field region-select-field"
        :placeholder="$t('form.selectRegion') || '选择地区/州'"
      />
    </div>
  </div>
        
        <!-- 提交按钮 -->
        <button 
          type="submit" 
          class="submit-button" 
          :disabled="isSubmitting"
        >
          <span class="button-text">{{ isSubmitting ? $t('form.submitting') : $t('form.submit') }}</span>
          <i class="fa fa-arrow-right button-icon"></i>
        </button>
        <!-- 错误信息 -->
        <div class="error-message" :class="{ show: errorMessage }">
          <i class="fa fa-exclamation-circle error-icon"></i>
          <span class="error-text">{{ errorMessage }}</span>
        </div>
        <!-- 隐私声明 -->
        <div class="privacy-notice">
          <i class="fa fa-shield-alt privacy-icon"></i>
          <span class="privacy-text" v-html="$t('form.privacy')"></span>
        </div>
      </form>
    </div>
    
    <!-- 加载遮罩 -->
    <div class="loading-overlay" :class="{ show: isSubmitting }">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ $t('form.loading') || 'Calculating your fortune...' }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { callFortuneAPI } from '../api/fortune.js'

const { t, locale } = useI18n()

const emit = defineEmits(['submit', 'api-error'])

const form = ref({
  name: '',
  gender: 'male',
  birthDate: '',
  birthPlace: {
    country: '',
    province: '',
    city: '',
    countryName: '',
    provinceName: '',
    cityName: '',
    fullName: ''
  }
})

// 日期时间输入
const birthDateTime = ref('')

// 出生地选择（vue3-country-region-select 内置数据）
const countrySelect2 = reactive({
  country: '',
  region: ''
})

// 监听 countrySelect2.country 变化，同步到 form.birthPlace
watch(() => countrySelect2.country, (newCountry) => {
  if (newCountry) {
    // country-select 组件 :countryName="true" 返回国家全名
    form.value.birthPlace.country = newCountry
    form.value.birthPlace.countryName = newCountry
    // 清空地区
    countrySelect2.region = ''
    form.value.birthPlace.province = ''
    form.value.birthPlace.provinceName = ''
    form.value.birthPlace.city = ''
    form.value.birthPlace.cityName = ''
    form.value.birthPlace.fullName = newCountry
  } else {
    form.value.birthPlace.country = ''
    form.value.birthPlace.countryName = ''
    form.value.birthPlace.province = ''
    form.value.birthPlace.provinceName = ''
    form.value.birthPlace.city = ''
    form.value.birthPlace.cityName = ''
    form.value.birthPlace.fullName = ''
  }
})

// 监听 countrySelect2.region 变化，同步到 form.birthPlace
watch(() => countrySelect2.region, (newRegion) => {
  if (newRegion) {
    form.value.birthPlace.province = newRegion
    form.value.birthPlace.provinceName = newRegion
    // 更新完整名称
    const countryName = form.value.birthPlace.countryName || ''
    form.value.birthPlace.fullName = [countryName, newRegion].filter(Boolean).join(' - ')
  } else {
    form.value.birthPlace.province = ''
    form.value.birthPlace.provinceName = ''
    form.value.birthPlace.city = ''
    form.value.birthPlace.cityName = ''
    form.value.birthPlace.fullName = form.value.birthPlace.countryName || ''
  }
})

// 日期选择器ref
const datePickerInput = ref(null)

// 日期范围限制
const minDate = ref('1900-01-01')
const maxDate = ref('')

const errorMessage = ref('')
const isSubmitting = ref(false)

// 检测系统语言并设置默认语言
const detectSystemLanguage = () => {
  const systemLang = navigator.language || navigator.userLanguage || 'en'
  
  // 检查是否为简体中文
  if (systemLang.startsWith('zh')) {
    return 'zh'
  }
  // 检查是否为英语
  else if (systemLang.startsWith('en')) {
    return 'en'
  }
  // 其他语言默认显示西班牙语
  else {
    return 'es'
  }
}

const selectedLanguage = ref(detectSystemLanguage())

// 计算属性：日期时间占位符（根据语言）
const dateTimePlaceholder = computed(() => {
  switch(selectedLanguage.value) {
    case 'zh': return '选择出生日期和时间'
    case 'en': return 'Select birth date and time'
    case 'es': return 'Seleccionar fecha y hora de nacimiento'
    default: return 'Select birth date and time'
  }
})

// 监听 birthDateTime 变化，更新 form.birthDate
watch(birthDateTime, (newValue) => {
  if (newValue) {
    // 将日期对象转换为ISO字符串
    const date = new Date(newValue)
    form.value.birthDate = date.toISOString()
  } else {
    form.value.birthDate = ''
  }
})


// 根据所选语言返回日期输入的语言代码（用于设置文档语言）
const dateInputLang = computed(() => {
  switch(selectedLanguage.value) {
    case 'zh': return 'zh-CN' // 中文（中国大陆）
    case 'en': return 'en-US' // 英语（美国）
    case 'es': return 'es-ES' // 西班牙语（西班牙）
    default: return 'en-US'
  }
})

// 处理日期时间输入变化
const handleDateTimeChange = () => {
  if (birthDateTime.value) {
    // 将datetime-local格式转换为ISO字符串
    const date = new Date(birthDateTime.value)
    form.value.birthDate = date.toISOString()
  } else {
    form.value.birthDate = ''
  }
}

// 更新显示的日期格式
const updateDisplayDate = (dateString) => {
  // 空值不处理
  if (!dateString) return
}

const changeLanguage = () => {
  locale.value = selectedLanguage.value
  // 保存语言选择到localStorage
  localStorage.setItem('userLocale', selectedLanguage.value)
  
  // 设置整个文档的语言，以影响日期输入
  document.documentElement.lang = dateInputLang.value
  
  // 更新日期显示格式
  if (birthDateTime.value) {
    updateDisplayDate(birthDateTime.value)
  }
}

onMounted(() => {
  // 使用当前vue-i18n的locale（已经从localStorage或系统语言初始化）
  // 而不是重新检测系统语言，以保持语言一致性
  selectedLanguage.value = locale.value
  
  // 设置初始文档语言
  document.documentElement.lang = dateInputLang.value
  
  // 设置最大日期为今天
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  maxDate.value = `${year}-${month}-${day}`
})


// 将datetime-local格式转换为API所需的格式
// 支持：YYYYMMDD（8位）、YYYYMMDDHH（10位）、YYYYMMDDHHMM（12位）中文模式
// 支持：DDMMYYYY（8位）、HHDDMMYYYY（10位）、HHMMDDMMYYYY（12位）英文/西语模式
const formatDateForAPI = (datetimeString, language = 'zh') => {
  if (!datetimeString) return '';
  
  try {
    // 改进的日期解析：处理微信浏览器可能的不同格式
    let date;
    
    // 尝试解析ISO格式 (YYYY-MM-DDTHH:mm)
    if (datetimeString.includes('T')) {
      date = new Date(datetimeString);
    }
    // 尝试解析日期部分 (YYYY-MM-DD)
    else if (datetimeString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      date = new Date(datetimeString + 'T00:00:00');
    }
    // 尝试解析其他格式
    else {
      date = new Date(datetimeString);
    }
    
    // 如果日期无效，尝试手动解析
    if (isNaN(date.getTime())) {
      console.warn('标准日期解析失败，尝试手动解析:', datetimeString);
      
      // 尝试解析 YYYY-MM-DD HH:mm 格式
      const match = datetimeString.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
      if (match) {
        const [, year, month, day, hour, minute] = match;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
      } else {
        // 最后尝试使用Date.parse
        const timestamp = Date.parse(datetimeString);
        if (!isNaN(timestamp)) {
          date = new Date(timestamp);
        } else {
          throw new Error('无法解析日期格式: ' + datetimeString);
        }
      }
    }
    
    // 获取年月日（使用本地时间，避免时区问题）
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    // 获取小时和分钟（24小时制）
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    
    console.log('日期解析结果:', { datetimeString, year, month, day, hour, minute, language });
    
    if (language === 'zh') {
      // 中文模式：YYYYMMDD 或 YYYYMMDDHH 或 YYYYMMDDHHMM
      // 如果小时和分钟都是00，则只返回年月日
      if (hour === '00' && minute === '00') {
        return `${year}${month}${day}`; // 8位
      } else if (minute === '00') {
        return `${year}${month}${day}${hour}`; // 10位
      } else {
        return `${year}${month}${day}${hour}${minute}`; // 12位
      }
    } else {
      // 英语/西班牙语模式：DDMMYYYY 或 HHDDMMYYYY 或 HHMMDDMMYYYY
      if (hour === '00' && minute === '00') {
        return `${day}${month}${year}`; // 8位
      } else if (minute === '00') {
        return `${hour}${day}${month}${year}`; // 10位
      } else {
        return `${hour}${minute}${day}${month}${year}`; // 12位
      }
    }
  } catch (error) {
    console.error('日期格式转换错误:', error, '输入:', datetimeString);
    return '';
  }
}

const validateBirthtime = (birthtime, language = 'zh') => {
  // 检查是否为空
  if (!birthtime || !birthtime.trim()) {
    return { valid: false, message: t('form.validation.dateRequired') }
  }
  
  // 验证是否为有效的日期时间字符串（datetime-local格式）
  try {
    const date = new Date(birthtime);
    if (isNaN(date.getTime())) {
      return { valid: false, message: t('form.validation.invalidDate') }
    }
    
    // 检查日期是否合理（不能是未来日期）
    const now = new Date();
    if (date > now) {
      return { valid: false, message: '出生日期不能是未来日期' }
    }
    
    // 检查日期是否太早（比如1900年以前）
    const minDate = new Date('1900-01-01');
    if (date < minDate) {
      return { valid: false, message: '出生日期不能早于1900年' }
    }
    
    // 转换为API所需的格式
    const normalizedDate = formatDateForAPI(birthtime, language);
    
    if (!normalizedDate) {
      return { valid: false, message: t('form.validation.invalidDate') }
    }
    
    return { valid: true, normalizedDate }
  } catch (error) {
    console.error('日期验证错误:', error);
    return { valid: false, message: t('form.validation.invalidDate') }
  }
}

const showError = (message) => {
  errorMessage.value = message
  setTimeout(() => {
    errorMessage.value = ''
  }, 5000)
}


const handleSubmit = async () => {
  console.log('=== handleSubmit called ===')
  
  // 防止重复提交
  if (isSubmitting.value) {
    console.log('Submission blocked: already submitting')
    return
  }
  
  isSubmitting.value = true
  errorMessage.value = ''
  
  try {
    // 验证表单
    if (!form.value.name.trim()) {
      console.log('Validation failed: name is empty')
      showError(t('form.validation.nameRequired'))
      return
    }
    
    if (form.value.name.length < 2 || form.value.name.length > 50) {
      console.log('Validation failed: name length invalid')
      showError(t('form.validation.nameLength') || '姓名长度必须在2到50个字符之间')
      return
    }
    
    if (!form.value.birthDate.trim()) {
      console.log('Validation failed: birthDate is empty')
      showError(t('form.validation.dateRequired'))
      return
    }
    
    
    // 验证出生地（至少选择国家）
    if (!form.value.birthPlace.country) {
      console.log('Validation failed: birth place country is required')
      showError(t('form.validation.birthPlaceRequired') || '请选择出生国家')
      return
    }
    
    console.log('Validating birthtime:', form.value.birthDate)
    const validation = validateBirthtime(form.value.birthDate, selectedLanguage.value)
    if (!validation.valid) {
      console.log('Validation failed:', validation.message)
      showError(validation.message)
      return
    }
    
    console.log('Validation passed, normalized date:', validation.normalizedDate)
    
    console.log('Birth place before submit:', form.value.birthPlace)

    console.log('Calling fortune API with:', {
      name: form.value.name,
      gender: form.value.gender,
      birthDate: validation.normalizedDate,
      birthPlace: form.value.birthPlace,
      language: selectedLanguage.value
    })
    
    // 第一步：调用SunCoreOline API（端口5042）保存用户数据并获取userId
    let userId = null
    let suncoreResponse = null
    try {
      suncoreResponse = await callFortuneAPI(
        form.value.name,
        form.value.gender,
        validation.normalizedDate,
        form.value.birthPlace,
        selectedLanguage.value,
        {
          country: countrySelect2.country,
          region: countrySelect2.region
        }
      )
      console.log('SunCoreOline API response:', suncoreResponse)

      // 从API响应中提取userId
      if (suncoreResponse && suncoreResponse.success && suncoreResponse.data && suncoreResponse.data.userId) {
        userId = suncoreResponse.data.userId
      } else if (suncoreResponse && suncoreResponse.userId) {
        userId = suncoreResponse.userId
      } else if (suncoreResponse && suncoreResponse.data && suncoreResponse.data.userId) {
        userId = suncoreResponse.data.userId
      }
      
      // 如果获取到userId，保存到localStorage
      if (userId) {
        localStorage.setItem('userId', userId)
        console.log('User ID from SunCoreOline API response:', userId)
      }
    } catch (suncoreError) {
      console.warn('SunCoreOline API调用失败（不影响运势计算）:', suncoreError.message)
      // SunCoreOline失败不影响运势计算，继续执行
    }
    
    // 第二步：调用本地Fortune Server（端口3001）获取完整的运势数据
    console.log('Calling local fortune server (port 3001) for complete fortune data...')
    let fortuneResult = null
    try {
      // 构建预测日期（当前日期，YYYYMMDD格式）
      const now = new Date()
      const predictionDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
      
      const fortuneServerUrl = getFortuneServerUrl()
      const fortuneResponse = await fetch(`${fortuneServerUrl}/web-api/fortune/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.value.name,
          gender: form.value.gender,
          birthDate: validation.normalizedDate,
          predictionDate: predictionDate,
          language: selectedLanguage.value
        })
      })
      
      if (fortuneResponse.ok) {
        const fortuneData = await fortuneResponse.json()
        console.log('Local fortune server response:', fortuneData)
        
        if (fortuneData.success && fortuneData.data) {
          fortuneResult = fortuneData.data
        } else if (fortuneData.data) {
          fortuneResult = fortuneData.data
        } else {
          fortuneResult = fortuneData
        }
      } else {
        console.warn('Local fortune server returned error:', fortuneResponse.status)
      }
    } catch (fortuneServerError) {
      console.error('Local fortune server调用失败:', fortuneServerError.message)
      // 如果本地fortune server也失败，使用SunCoreOline的返回数据（如果有）
    }
    
    // 第三步：合并数据 - 优先使用本地fortune server的完整运势数据
    let mergedFortuneData = null
    
    if (fortuneResult) {
      // 使用本地fortune server的完整数据
      mergedFortuneData = fortuneResult
      console.log('Using fortune data from local server (port 3001)')
    } else if (suncoreResponse) {
      // 降级：使用SunCoreOline返回的数据
      mergedFortuneData = suncoreResponse.data || suncoreResponse
      console.log('Falling back to SunCoreOline data')
    }

    // 构建符合MainPage期望的数据格式
    const responseData = {
      formData: {
        name: form.value.name,
        gender: form.value.gender,
        birthDate: validation.normalizedDate,
        birthPlace: form.value.birthPlace,
        language: selectedLanguage.value,
        userId: userId
      },
      fortuneData: mergedFortuneData
    }

    console.log('Final response data:', responseData)
    emit('submit', responseData)
  } catch (error) {
    console.error('提交错误:', error)
    showError(error.message || t('form.submitError'))
    emit('api-error', error)
  } finally {
    isSubmitting.value = false
  }
}

// 获取本地Fortune Server的基础URL
const getFortuneServerUrl = () => {
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  
  if (isDevelopment) {
    return 'http://localhost:3001'
  }
  
  // 生产环境使用相对路径（避免跨域问题）
  return ''
}

// 获取基础API URL
const getBaseApiUrl = () => {
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  if (isDevelopment) {
    return 'http://localhost:5042'
  }
  // 生产环境使用相对路径，通过Nginx代理到SunCoreOline
  return ''
}
</script>

<style scoped>
/* 无印良品风格设计系统 */
.fortune-form {
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background-color: #F9F7F4;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
}

/* 表单容器 */
.form-container {
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 40px 30px;
  border: 1px solid #E8E6E1;
  box-sizing: border-box;
}

/* 头部区域 */
.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
}

.form-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: #8B7355;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.logo-text {
  font-size: 24px;
  font-weight: 300;
  color: #333;
}

.form-title {
  font-size: 28px;
  font-weight: 400;
  color: #333;
  margin-bottom: 8px;
}

.form-subtitle {
  font-size: 16px;
  color: #666;
  font-weight: 300;
  line-height: 1.5;
}

/* 语言选择器 */
.language-selector-wrapper {
  position: relative;
  width: fit-content;
  max-width: 100%;
}

.language-selector {
  padding: 8px 40px 8px 16px;
  border: 1px solid #E8E6E1;
  border-radius: 8px;
  background: white;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  min-width: 120px;
  box-sizing: border-box;
}

.language-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  pointer-events: none;
  font-size: 14px;
}

/* 表单主体 */
.form-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
}

/* 输入组 */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.input-icon {
  color: #8B7355;
  font-size: 14px;
  flex-shrink: 0;
}

.input-field {
  padding: 14px 16px;
  border: 1px solid #E8E6E1;
  border-radius: 8px;
  background: white;
  color: #333;
  font-size: 16px;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.input-field:focus {
  border-color: #8B7355;
  box-shadow: 0 0 0 3px rgba(139, 115, 85, 0.1);
  outline: none;
}

.input-hint {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

/* 日期时间输入容器 */
.datetime-inputs {
  display: flex;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
}

.datetime-input {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  /* 新增：禁用默认文本选择 */
  user-select: none;
  max-width: 100%;
}

/* 性别选项 */
.gender-options {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  width: 100%;
  box-sizing: border-box;
}

.gender-option {
  flex: 1;
  padding: 16px;
  border: 1px solid #E8E6E1;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.gender-option:hover {
  border-color: #8B7355;
  background: #F5F3F0;
}

.gender-option.active {
  border-color: #8B7355;
  background: rgba(139, 115, 85, 0.05);
}

.gender-option-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #F5F3F0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8B7355;
  font-size: 14px;
}

.gender-option.active .gender-option-icon {
  background: #8B7355;
  color: white;
}

.gender-option-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* 出生地选择器2（vue3-country-region-select） */
.birthplace-selector2 {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
}

.birthplace-selector2 .input-field {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* 确保country-select和region-select组件不溢出 */
.birthplace-selector2 :deep(.country-select-field),
.birthplace-selector2 :deep(.region-select-field) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

.birthplace-selector2 :deep(select),
.birthplace-selector2 :deep(.v-select),
.birthplace-selector2 :deep(.vs__dropdown-toggle) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

/* 错误信息 */
.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(211, 47, 47, 0.05);
  border: 1px solid rgba(211, 47, 47, 0.2);
  border-radius: 8px;
  color: #D32F2F;
  font-size: 14px;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s ease;
}

.error-message.show {
  opacity: 1;
  transform: translateY(0);
}

.error-icon {
  font-size: 16px;
}

/* 提交按钮 */
.submit-button {
  padding: 16px 24px;
  background: #8B7355;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
}

.submit-button:hover:not(:disabled) {
  background: #7A6348;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 隐私声明 */
.privacy-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(139, 115, 85, 0.05);
  border-radius: 8px;
  font-size: 13px;
  color: #666;
  margin-top: 24px;
  line-height: 1.5;
}

.privacy-icon {
  color: #8B7355;
  font-size: 16px;
}

/* 底部装饰 */
.form-footer {
  text-align: center;
  margin-top: 30px;
  padding-top: 24px;
  border-top: 1px solid #E8E6E1;
  color: #666;
  font-size: 14px;
}

/* 加载遮罩 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.loading-overlay.show {
  opacity: 1;
  visibility: visible;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #E8E6E1;
  border-top: 4px solid #8B7355;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: #333;
}

/* 响应式设计 - 手机端适配 */
/* 确保所有屏幕尺寸下都不会横向滚动 */
@media (max-width: 768px) {
  .fortune-form {
    padding: 12px;
  }
  
  .form-container {
    padding: 28px 20px;
    border-radius: 16px;
  }
  
  .form-title {
    font-size: 24px;
  }
  
  .gender-options {
    flex-direction: row;
  }
  
  .form-body {
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .fortune-form {
    padding: 10px;
  }
  
  .form-container {
    padding: 24px 16px;
    border-radius: 14px;
  }
  
  .form-title {
    font-size: 22px;
  }
  
  .input-field {
    padding: 12px 14px;
    font-size: 15px;
  }
  
  .gender-options {
    gap: 10px;
  }
  
  .gender-option {
    padding: 12px 10px;
  }
  
  .submit-button {
    padding: 14px 20px;
    font-size: 15px;
  }
  
  .form-body {
    gap: 18px;
  }
  
  /* 手机端日期时间输入优化 */
  .datetime-inputs {
    flex-direction: row;
    gap: 8px;
  }
  
  .datetime-input {
    min-width: 0;
    flex: 1;
  }
  
  /* 语言选择器优化 - 手机端换行显示 */
  .form-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .form-logo {
    justify-content: center;
    text-align: center;
  }
  
  .language-selector-wrapper {
    width: 100%;
  }
  
  .language-selector {
    width: 100%;
    min-width: 100%;
  }
  
  /* 出生地选择器手机端优化 */
  .birthplace-selector2 {
    gap: 8px;
  }
  
  .privacy-notice {
    padding: 12px;
    margin-top: 18px;
  }
  
  .error-message {
    padding: 12px;
  }
}

@media (max-width: 380px) {
  .fortune-form {
    padding: 8px;
  }
  
  .form-container {
    padding: 20px 12px;
    border-radius: 12px;
  }
  
  .form-title {
    font-size: 20px;
  }
  
  .form-header {
    gap: 10px;
    margin-bottom: 20px;
  }
  
  .gender-options {
    gap: 8px;
  }
  
  .gender-option {
    padding: 10px 8px;
  }
  
  .gender-option-icon {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  
  .gender-option-label {
    font-size: 13px;
  }
  
  .input-field {
    padding: 10px 12px;
    font-size: 14px;
  }
  
  .submit-button {
    padding: 12px 16px;
    font-size: 14px;
  }
  
  .form-body {
    gap: 16px;
  }
  
  .datetime-inputs {
    gap: 6px;
  }
  
  .birthplace-selector2 {
    gap: 6px;
  }
  
  .privacy-notice {
    padding: 10px;
    margin-top: 14px;
    font-size: 12px;
  }
  
  .error-message {
    padding: 10px;
    font-size: 13px;
  }
  
  .loading-text {
    font-size: 14px;
  }
  
  .loading-spinner {
    width: 40px;
    height: 40px;
  }
}

/* 超小屏幕（如 iPhone SE 等） */
@media (max-width: 320px) {
  .fortune-form {
    padding: 6px;
  }
  
  .form-container {
    padding: 16px 10px;
    border-radius: 10px;
  }
  
  .form-title {
    font-size: 18px;
  }
  
  .gender-options {
    gap: 6px;
  }
  
  .gender-option {
    padding: 8px 6px;
  }
  
  .gender-option-icon {
    width: 24px;
    height: 24px;
    font-size: 11px;
  }
  
  .gender-option-label {
    font-size: 12px;
  }
  
  .input-field {
    padding: 8px 10px;
    font-size: 13px;
  }
  
  .submit-button {
    padding: 10px 14px;
    font-size: 13px;
  }
  
  .form-body {
    gap: 14px;
  }
  
  .datetime-inputs {
    flex-direction: column;
    gap: 6px;
  }
  
  .datetime-input {
    width: 100%;
  }
  
  .birthplace-selector2 {
    gap: 6px;
  }
  
  .privacy-notice {
    padding: 8px;
    margin-top: 12px;
    font-size: 11px;
  }
}

/* 日期输入框样式优化 */
input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(0.3);
  cursor: pointer;
}

input[type="date"]::-webkit-datetime-edit {
  padding: 2px;
}

input[type="date"]::-webkit-datetime-edit-fields-wrapper {
  display: flex;
}

input[type="date"]::-webkit-datetime-edit-text {
  padding: 0 4px;
}

input[type="date"]::-webkit-datetime-edit-month-field,
input[type="date"]::-webkit-datetime-edit-day-field,
input[type="date"]::-webkit-datetime-edit-year-field {
  padding: 2px 6px;
}

/* 不同语言日历样式优化 */
input[type="date"][lang="zh-CN"] {
  /* 中文日历样式优化 */
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
}

input[type="date"][lang="en-US"],
input[type="date"][lang="es-ES"] {
  /* 英文/西语日历样式优化 */
  font-family: "Arial", "Helvetica", sans-serif;
}
</style>
